/**
 * @fileoverview Global notification settings dialog (MOR-716).
 *
 * Two master channel switches plus the per-category matrix. Saves on every
 * toggle (Convex reactivity keeps the UI in sync). Per-task overrides from
 * the bell on any task win over these defaults.
 */

import { useMutation, useQuery } from "convex/react";
import { api } from "@cvx/_generated/api";
import { Switch } from "@/ui/primitives/switch";
import { Label } from "@/ui/primitives/label";
import { Separator } from "@/ui/primitives/separator";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/ui/composed/overlays/dialog";
import {
  CategoryMatrix,
  type NotificationCategoryKey,
} from "./category-matrix";

export function NotificationSettingsDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const prefs = useQuery(api.notifications.preferences.getMine);
  const setMine = useMutation(api.notifications.preferences.setMine);

  if (!prefs) return null;

  const toggleMaster = (channel: "inAppEnabled" | "emailEnabled") =>
    void setMine({ ...prefs, [channel]: !prefs[channel] });

  const toggleCategory = (
    key: NotificationCategoryKey,
    channel: "inApp" | "email",
  ) =>
    void setMine({
      ...prefs,
      matrix: {
        ...prefs.matrix,
        [key]: { ...prefs.matrix[key], [channel]: !prefs.matrix[key][channel] },
      },
    });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Notification settings</DialogTitle>
          <DialogDescription>
            Choose how you're notified about activity on your tasks.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <Label htmlFor="inapp-master">In-app notifications</Label>
            <Switch
              id="inapp-master"
              checked={prefs.inAppEnabled}
              onCheckedChange={() => toggleMaster("inAppEnabled")}
            />
          </div>
          <div className="flex items-center justify-between">
            <Label htmlFor="email-master">Email notifications</Label>
            <Switch
              id="email-master"
              checked={prefs.emailEnabled}
              onCheckedChange={() => toggleMaster("emailEnabled")}
            />
          </div>

          <Separator />

          <CategoryMatrix matrix={prefs.matrix} onToggle={toggleCategory} />

          <p className="text-xs text-muted-foreground">
            Per-task settings from the bell on any task override these defaults.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
